import React from 'react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiCpu, FiPlay, FiPause, FiCheckCircle, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

export default function AIWorkforce() {
  return (
    <div className="max-w-7xl mx-auto p-8">
      <header className="mb-8 flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">AI Workforce Control Center</h1>
          <p className="text-slate-600 mt-1">Orchestrating agent execution, governance, and human approvals.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" className="gap-2"><SafeIcon icon={FiRefreshCw} /> Refresh Status</Button>
          <Button variant="primary" className="gap-2"><SafeIcon icon={FiPlay} /> Resume All Paused</Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard title="Active Agents" value="12" subtitle="Across 4 missions" />
        <StatsCard title="Pending Approvals" value="5" subtitle="Human review required" alert />
        <StatsCard title="Tasks Completed" value="142" subtitle="Last 7 days" />
        <StatsCard title="System Health" value="98%" subtitle="API stability" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 flex justify-between items-center">
          <h2 className="font-semibold text-slate-800">Active Workflows</h2>
          <div className="flex gap-2">
            <span className="text-xs text-slate-500 font-medium">Filter by:</span>
            <select className="text-xs bg-white border border-slate-200 rounded px-2 py-1 outline-none">
              <option>All Missions</option>
              <option>EMEA Launch</option>
            </select>
          </div>
        </div>
        
        <div className="divide-y divide-slate-100">
          <AgentRow 
            agent="Strategy Agent"
            mission="EMEA Launch"
            task="Drafting initial market positioning based on Q2 Research"
            status="running"
            confidence="92%"
          />
          <AgentRow 
            agent="Content Agent"
            mission="EMEA Launch"
            task="Generating localized landing page copy (FR, DE)"
            status="awaiting-review"
            confidence="88%"
          />
          <AgentRow 
            agent="SEO Agent"
            mission="Q3 Pipeline"
            task="Analyzing keyword gaps for new enterprise features"
            status="error"
            confidence="N/A"
          />
          <AgentRow 
            agent="Analytics Agent"
            mission="CAC Reduction"
            task="Compiling Experiment 402 results and interpreting statistical significance"
            status="completed"
            confidence="95%"
          />
        </div>
      </div>
    </div>
  );
}

function StatsCard({ title, value, subtitle, alert }) {
  return (
    <Card className="p-5 border-t-4 border-t-slate-800">
      <h3 className="text-sm font-medium text-slate-500 mb-1">{title}</h3>
      <div className="text-3xl font-semibold text-slate-900 mb-1 flex items-center gap-2">
        {value}
        {alert && <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>}
      </div>
      <p className="text-xs text-slate-400">{subtitle}</p>
    </Card>
  );
}

function AgentRow({ agent, mission, task, status, confidence }) {
  const statusConfig = {
    'running': { icon: FiRefreshCw, color: 'text-blue-500', bg: 'bg-blue-50', text: 'Executing', spin: true },
    'awaiting-review': { icon: FiAlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', text: 'Needs Approval', spin: false },
    'error': { icon: FiAlertTriangle, color: 'text-red-500', bg: 'bg-red-50', text: 'Blocked', spin: false },
    'completed': { icon: FiCheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', text: 'Completed', spin: false },
  };

  const config = statusConfig[status];

  return (
    <div className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group">
      <div className="flex items-center gap-4 flex-1">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bg} ${config.color}`}>
          <SafeIcon icon={FiCpu} className="text-xl" />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-slate-900 text-sm">{agent}</h4>
            <span className="text-xs text-slate-500 px-1.5 py-0.5 bg-slate-100 rounded">{mission}</span>
          </div>
          <p className="text-sm text-slate-600 truncate max-w-xl">{task}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-8">
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</div>
          <div className={`flex items-center justify-end gap-1.5 text-sm font-medium ${config.color}`}>
            <SafeIcon icon={config.icon} className={config.spin ? 'animate-spin' : ''} />
            {config.text}
          </div>
        </div>
        
        <div className="text-right w-20">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Confidence</div>
          <div className="text-sm font-medium text-slate-800">{confidence}</div>
        </div>

        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {status === 'running' && <Button variant="secondary" size="sm" className="px-2"><SafeIcon icon={FiPause} /></Button>}
          {status === 'awaiting-review' && <Button variant="primary" size="sm">Review</Button>}
          {status === 'error' && <Button variant="secondary" size="sm">View Logs</Button>}
        </div>
      </div>
    </div>
  );
}